import "./SelectX.css"
import equal from "fast-deep-equal"

import {
  forwardRef,
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from "react"
import { v4 as uuid } from "uuid"
import classNames from "classnames"

import {
  isAValue,
  isNotAValue,
  isObjectWithProperty,
} from "web/services/helpers"

import useCombinedRefs from "web/hooks/behaviour/useCombinedRefs"

import Suggestions, { QUICK_ADD_VALUE } from "./Suggestions"
import Selections from "./Selections"

function getInputValue(value, multipleValues) {
  if (multipleValues || !isObjectWithProperty(value, "label")) {
    return ""
  }

  return value.label
}

function getValidValue(value, multipleValues) {
  if (multipleValues && isNotAValue(value)) return []
  return value
}

function getSuggestionFunction(validValues, suggestedValues) {
  function constantValidValues(inputValue) {
    if (inputValue === "") return validValues

    return validValues.filter((validValue) =>
      validValue.label.toLowerCase().includes(inputValue.toLowerCase())
    )
  }

  if (typeof validValues === "function") {
    return validValues
  }

  if (validValues !== undefined) {
    return constantValidValues
  }

  if (typeof suggestedValues === "function") {
    return suggestedValues
  }

  return null
}

const SelectX = forwardRef(
  (
    {
      id = uuid(),
      name = "",
      value: valueFromProps,
      validValues,
      suggestedValues,
      suggestionIcons,
      suggestionComponent,
      onAdd,
      onChange,
      onFocus,
      options = {},
      disabled = false,
    },
    ref
  ) => {
    const {
      multipleValues = false,
      includeNullSuggestion = true,
      clearOnFocus = true,
      inputClassName = null,
      inputTestId,
      autoFocus,
      placeholder = "",
      sortSuggestions,
      suggestionDelay,
      optionsLoading,
    } = options

    const inputId = useRef(`select-${id}`)
    const allowQuickAdd = typeof onAdd === "function"

    const suggestionFunction = useMemo(
      () => getSuggestionFunction(validValues, suggestedValues),
      [validValues, suggestedValues]
    )
    const hasSuggestionFunction = suggestionFunction !== null

    const SUGGESTION_LIST_ID = `${inputId.current}-suggestions-list`

    const innerRef = useRef()
    const inputRef = useCombinedRefs(ref, innerRef)
    const inputInFocus =
      inputRef.current && inputRef.current === document.activeElement

    const [value, setValue] = useState(
      getValidValue(valueFromProps, multipleValues)
    )

    const valueRef = useRef(value)

    const [inputValue, setInputValue] = useState(
      getInputValue(valueFromProps, multipleValues)
    )

    const editing = useRef(false)

    function startEditing() {
      editing.current = true
    }

    function stopEditing() {
      editing.current = false
    }

    function isEditing() {
      return editing.current
    }

    const [, updateState] = useState()
    const forceUpdate = useCallback(() => updateState({}), [])

    function _onChange(newValue) {
      if (!onChange) return

      valueRef.current = newValue

      if (
        (multipleValues && newValue.length === 0) ||
        newValue.value === null
      ) {
        onChange(null)
        return
      }

      onChange(newValue)
    }

    function valueIncludesItem(item) {
      return value.map((v) => v.value).includes(item.value)
    }

    function addItems(items) {
      const itemsToAdd = []

      for (const item of items) {
        if (valueIncludesItem(item)) continue
        itemsToAdd.push(item)
      }

      if (itemsToAdd.length !== 0) {
        const newValue = [...value, ...itemsToAdd]
        setValue(newValue)
        _onChange(newValue)
      }
    }

    function handleRemoveItem(index) {
      const newValue = [...value.slice(0, index), ...value.slice(index + 1)]

      setValue(newValue)
      _onChange(newValue)
    }

    function handleInputKeyDown(e) {
      if (!["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)) return

      e.preventDefault()
      e.stopPropagation()

      if (e.key === "Escape") {
        inputRef.current.blur()
        return
      }

      if (e.key === "Enter" && !hasSuggestionFunction) {
        addItems([{ value: inputValue, label: inputValue }])
        return
      }

      const suggestionListElement = document.getElementById(SUGGESTION_LIST_ID)
      if (!suggestionListElement) return

      suggestionListElement.dispatchEvent(
        new CustomEvent("div_keydown", { detail: e.key })
      )
    }

    function handleInputFocus(e) {
      startEditing()

      if (clearOnFocus && inputValue !== "") {
        setInputValue("")
      } else {
        forceUpdate()
      }
      if (onFocus) onFocus(e)
    }

    const inputBlurTimeoutHandle = useRef()

    useEffect(() => {
      return () => {
        if (inputBlurTimeoutHandle.current) {
          clearTimeout(inputBlurTimeoutHandle.current)
        }
      }
    }, [])

    function handleInputBlur() {
      stopEditing()

      inputBlurTimeoutHandle.current = setTimeout(() => {
        if (isEditing()) return

        forceUpdate()

        // restore value in input on blur
        if (inputRef.current && inputRef.current.value === "") {
          const inputValue = getInputValue(valueRef.current, multipleValues)
          if (inputValue !== "") setInputValue(inputValue)
        }
      }, 200)
    }

    function handleInputChange(e) {
      e.preventDefault()

      const { value: inputValue } = e.target

      setInputValue(inputValue)
    }

    function handleChooseSuggestion(suggestion) {
      clearTimeout(inputBlurTimeoutHandle.current)

      if (suggestion.value === QUICK_ADD_VALUE) {
        onAdd(inputValue)
      } else if (multipleValues) {
        addItems([suggestion])
        setInputValue("")
      } else {
        setValue(suggestion)
        valueRef.current = suggestion

        setInputValue(suggestion.label === "-" ? "" : suggestion.label)
        _onChange(suggestion)
      }

      inputRef.current.blur()
    }

    useEffect(() => {
      if (autoFocus) {
        inputRef.current.focus()
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      const hadValue = isAValue(value)
      const hasValue = isAValue(valueFromProps)

      if (hadValue === hasValue && equal(value, valueFromProps)) return
      if (isEditing()) return

      const newValue = getValidValue(valueFromProps, multipleValues)
      setValue(newValue)
      valueRef.current = newValue

      setInputValue(getInputValue(valueFromProps, multipleValues))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueFromProps, multipleValues])

    const selectXClasses = classNames("selectx", {
      multi: multipleValues,
      "opacity-50": disabled,
    })

    return (
      <>
        <div className={selectXClasses}>
          <input
            type="text"
            id={inputId.current}
            autoComplete={`origin-field-${name}`}
            data-testid={inputTestId}
            ref={inputRef}
            onKeyDown={handleInputKeyDown}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            value={inputValue}
            className={inputClassName}
            placeholder={placeholder}
            disabled={disabled}
          />
          {inputInFocus && hasSuggestionFunction && (
            <Suggestions
              suggestionFunction={suggestionFunction}
              suggestionIcons={suggestionIcons}
              sortSuggestions={sortSuggestions}
              suggestionDelay={suggestionDelay}
              optionsLoading={optionsLoading}
              value={value}
              inputRef={inputRef}
              inputId={inputId}
              testId={inputTestId}
              inputValue={inputValue}
              multipleValues={multipleValues}
              allowQuickAdd={allowQuickAdd}
              includeNullSuggestion={includeNullSuggestion}
              onChooseSuggestion={handleChooseSuggestion}
              onAddItems={addItems}
              suggestionComponent={suggestionComponent}
            />
          )}
        </div>
        {multipleValues && (
          <Selections
            value={value}
            inputTestId={inputTestId}
            onRemoveItem={handleRemoveItem}
            deactivated={disabled}
          />
        )}
      </>
    )
  }
)

export default SelectX
